// The LOADOUT sheet of a Sapper in the pens (v2-the-deep.md §11.1, §11.9,
// R2654, R2658; ui-map-interface.md §12 "FLEET · MISSIONS"). The code
// keeps the name STOW sheet; the player reads LOADOUT, and STOW is the
// press that sends the draft (R2703).
//
// The stow a Sapper loads at its next deploy is one picker card of the kit,
// as the LOADOUT sheet of a tubed hull is (R2031): the context slot of a
// desktop, a page of the menu sheet on a telephone. LOADOUT in ⋯ of the pen
// row opens it, for a Sapper while the world latched `sapper`.
//
//   LOADOUT · Sapper 'Wire Kestrel'   60/100
//   BARB          ‹ 3 › »  held 12
//   5 cargo
//   SEINE         ‹ 1 › »  held  2
//   20 cargo
//   The Depot holds all items in this loadout. The Sapper sails with ...
//   [ AUTO ]               [ STOW ]
//
// One row for each kind the Depot level opens, and for each kind of the
// saved stow: the name with the cargo of one item under it (R2708), a
// stepper of the choice kit, the MAX press » and the stock of the Depot.
// The Depot line under the rows stands for every draft (R2745). The status chip states the cargo of the draft
// against the cargo of the hull. The + of a row stands disabled when one
// more item of the kind does not fit the cargo, and MAX fills the free
// cargo with the kind of its row. AUTO sends an empty stow; STOW sends the
// draft as `FLEET_OP_SET_STOW` with `FleetAction.stow`. The deploy takes
// the stow out of the Depot stock, and a kind short in the stock sails
// with what the stock holds.
//
// The sheet stands on a Sapper alone and holds the draft to the cargo, so
// the two refusals of the server, `stow_not_a_sapper` and
// `stow_does_not_fit`, never answer a press of it. The server holds both
// rules (`Hub.setStow`).
//
// The module is pure: it reads the state and the draft and writes the
// card spec.

import { Building, ShipClass, type SeaObjectSpec, type ShipStock } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import type { CardSpec } from '../cards/card';
import { shipMarkSvg } from '../cards/marks';
import { stowCargo } from '../cards/sapper';
import { esc } from '../esc';
import type { SceneLook } from '../../render/scene-look';
import type { GameState } from '../../state';
import { depotHeld } from './depot';
import { countOf, totalOf, type Stock } from '../rounds';
import { copyStock, maxStock, stepStock, stockActions, stockRow } from './stockpicker';

/** Does the pen row of this hull carry STOW? A Sapper, while the world
 *  latched `sapper`. */
export function stowsAtPens(s: Pick<GameState, 'hasFeatureFlag'>, st: Pick<ShipStock, 'cls'>): boolean {
  return st.cls === ShipClass.SHIP_CLASS_SAPPER && s.hasFeatureFlag('sapper');
}

/** The kinds the sheet lists: every kind the Depot level opens, and every
 *  kind of the saved stow, in kind order. */
export function stowKinds(s: GameState, st: Pick<ShipStock, 'stow'>): SeaObjectSpec[] {
  const level = s.level(Building.BUILDING_DEPOT);
  const saved = new Set(st.stow.map((r) => r.kind));
  return (s.catalog?.seaObjects ?? [])
    .filter((k) => k.depotLevel <= level || saved.has(k.id))
    .sort((a, b) => a.id - b.id);
}

/** The cargo of the hull: the cargo of its class row. */
function cargoOf(s: GameState, cls: ShipClass): number {
  return s.ship(cls)?.cargo ?? 0;
}

/** The draft a STOW sheet opens with: the saved stow of the hull, or none
 *  while it loads AUTO. */
export function stowDraftOf(s: GameState, fleetId: number): Stock[] {
  const st = s.pennedHull(fleetId);
  return st ? copyStock(st.stow) : [];
}

/** Does one more item of `kind` fit the cargo of the hull? */
function fits(s: GameState, draft: readonly Stock[], kind: number, cargo: number): boolean {
  const one = s.seaObject(kind)?.cargo ?? 0;
  return one > 0 && stowCargo((k) => s.seaObject(k), draft) + one <= cargo;
}

/**
 * One step of a stepper: the draft with one item of `kind` more (+1) or
 * less (−1), clamped at 0 and at the cargo. A step that would break the
 * clamp returns the draft as it was.
 */
export function stepStow(s: GameState, fleetId: number, draft: readonly Stock[], kind: number, dir: number): Stock[] {
  const st = s.pennedHull(fleetId);
  if (!st) return [...draft];
  return stepStock(draft, kind, dir, stowKinds(s, st).map((k) => k.id),
    dir < 0 || fits(s, draft, kind, cargoOf(s, st.cls)));
}

/** The MAX press of a row: the draft with the free cargo filled with
 *  `kind`. The other rows keep their counts, and the stock of the Depot
 *  does not limit it, as it does not limit the +. */
export function maxStow(s: GameState, fleetId: number, draft: readonly Stock[], kind: number): Stock[] {
  const st = s.pennedHull(fleetId);
  if (!st) return [...draft];
  return maxStock(draft, kind, stowKinds(s, st).map((k) => k.id),
    cargoOf(s, st.cls) - stowCargo((k) => s.seaObject(k), draft), s.seaObject(kind)?.cargo ?? 0);
}

/** The saved stow as a line, `Barb 3 · Seine 1`, or AUTO. */
function stowLine(s: GameState, stow: readonly Stock[]): string {
  const rows = stow.filter((r) => r.count > 0);
  if (rows.length === 0) return S.word_auto;
  return rows.map((r) => S.stow_line_item({ name: s.seaObject(r.kind)?.name ?? '?', n: r.count })).join(' · ');
}

/**
 * The note of a sheet with rows (R2745): the AUTO sentence for an empty
 * draft, else the Depot line, which states whether the stock holds the
 * draft. The three sentences stand in one grid cell and only the one that
 * applies is visible, so the note keeps one height in every state and a
 * press of + never moves the rows or the presses. The short sentence takes
 * the highlight ink (`.hl`), with no change of size, weight or frame.
 */
function stowNote(empty: boolean, short: boolean): string {
  const say = (text: string, on: boolean, hl = false): string =>
    `<span class="${[on ? '' : 'off', hl ? 'hl' : ''].filter(Boolean).join(' ')}"`
    + `${on ? '' : ' aria-hidden="true"'}>${esc(text)}</span>`;
  return '<span class="ck-swap">'
    + say(S.stow_note_auto, empty)
    + say(S.stow_note_full, !empty && !short)
    + say(S.stow_note_short, !empty && short, true)
    + '</span>';
}

/**
 * The STOW sheet of one Sapper in the pens, or null when the hull left the
 * pens. The Depot line under the rows states whether the Depot stock holds
 * the draft; a short stock turns it to the highlight ink (R2745).
 */
export function stowSheetSpec(s: GameState, fleetId: number, draft: readonly Stock[], look?: SceneLook): CardSpec | null {
  const st = s.pennedHull(fleetId);
  if (!st) return null;
  const cargo = cargoOf(s, st.cls);
  const depot = s.empire?.depot;
  let short = false;
  const rows = stowKinds(s, st).map((k) => {
    const n = countOf(draft, k.id);
    const held = depotHeld(depot, k.id);
    if (n > held) short = true;
    const name = k.name.toUpperCase();
    const full = !fits(s, draft, k.id, cargo);
    return stockRow({
      kind: k.id, name, count: n, full, stepAct: 'stowstep', maxAct: 'stowmax',
      less: S.stow_less({ name }), more: S.stow_more({ name }), max: S.stow_max({ name }), held: S.stow_held({ n: held }),
      weight: S.stow_weight({ n: k.cargo }),
    });
  }).join('');
  const note = rows === '' ? esc(S.stow_note_no_depot) : stowNote(totalOf(draft) === 0, short);
  return {
    size: 'card', mark: shipMarkSvg(st.cls, look), title: S.stow_title({ hull: st.name }),
    ink: { faction: s.faction },
    kind: S.load_kind({ cls: (s.ship(st.cls)?.name ?? '?').toUpperCase(), now: stowLine(s, st.stow) }),
    status: { text: `${stowCargo((k) => s.seaObject(k), draft)}/${cargo}` },
    close: true, closeAct: 'pickclose',
    body: rows ? `<div class="ck-load">${rows}</div>` : undefined,
    note: { lines: [note] },
    actions: stockActions('stow', fleetId, draft, st.stow),
  };
}
